/**
 * Server-sent events → the query cache.
 *
 * The rules that keep this from being a nuisance rather than a feature:
 *
 *  - **One connection.** A module-level client is reference-counted, so ten
 *    components can call `useRealtime()` and the browser still opens one stream.
 *  - **Coalesce before applying.** A bulk update on the server produces a burst;
 *    the buffer collapses it to one write per record per frame instead of 200
 *    cache writes and 200 renders.
 *  - **Patch, don't refetch.** A row that is already on screen is edited in
 *    place. Only queries this client cannot evaluate (free-text search, saved
 *    views) are marked stale, and even then without an immediate network call.
 *  - **Reconcile membership.** If an update pushes a record out of the filter
 *    you are looking at, it leaves the list — a stale row is a lie.
 */

import type { QueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { matchesInquiry } from '@/lib/filters';
import {
  patchInquiryEverywhere,
  prependActivity,
  prependInquiry,
  prependNote,
  queryKeys,
  removeInquiryEverywhere,
  upsertTask,
  type ListKeyPart,
} from '@/lib/queries';
import type { ID, Inquiry, ISODate, Paginated, RealtimeEvent } from '@shared/types';
import type { InfiniteData } from '@tanstack/react-query';

import type { BoardResponse } from '@/lib/api';

/* ────────────────────────────────────────────────────────────────────────────
   Public state
   ──────────────────────────────────────────────────────────────────────────── */

export type RealtimeStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'offline';

export interface PresenceUser {
  id: ID;
  name: string;
  at: ISODate;
}

export interface RealtimeState {
  status: RealtimeStatus;
  /** Who else has this workspace open. */
  presence: PresenceUser[];
  /** Last time anything arrived, including heartbeats. */
  lastEventAt: ISODate | null;
  /** Consecutive failed connection attempts; 0 while healthy. */
  attempts: number;
}

const INITIAL_STATE: RealtimeState = {
  status: 'idle',
  presence: [],
  lastEventAt: null,
  attempts: 0,
};

/* ────────────────────────────────────────────────────────────────────────────
   Tuning
   ──────────────────────────────────────────────────────────────────────────── */

/** Long enough to catch a burst, short enough to feel instant. */
const FLUSH_MS = 80;
/** Flush early rather than let a big import queue up. */
const MAX_BUFFER = 60;
const BASE_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;
/** After this many failures we stop burning battery and wait to be woken. */
const MAX_ATTEMPTS = 8;

/* ────────────────────────────────────────────────────────────────────────────
   Client
   ──────────────────────────────────────────────────────────────────────────── */

type Listener = () => void;
type Pages<T> = InfiniteData<Paginated<T>>;

class RealtimeClient {
  private state: RealtimeState = INITIAL_STATE;
  private listeners = new Set<Listener>();
  private source: EventSource | null = null;
  private queryClient: QueryClient | null = null;
  private refs = 0;
  private lastEventId: string | undefined;
  private buffer: RealtimeEvent[] = [];
  private flushTimer: number | undefined;
  private retryTimer: number | undefined;
  private wakeBound = false;

  /* ── store ─────────────────────────────────────────────────────────────── */

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getState = (): RealtimeState => this.state;

  private setState(patch: Partial<RealtimeState>): void {
    const next = { ...this.state, ...patch };
    if (
      next.status === this.state.status &&
      next.presence === this.state.presence &&
      next.lastEventAt === this.state.lastEventAt &&
      next.attempts === this.state.attempts
    ) {
      return;
    }
    this.state = next;
    for (const listener of this.listeners) listener();
  }

  /* ── lifecycle ─────────────────────────────────────────────────────────── */

  /** Reference-counted connect. Returns the release function. */
  acquire(queryClient: QueryClient): () => void {
    this.queryClient = queryClient;
    this.refs += 1;
    if (this.refs === 1) this.connect();

    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.refs -= 1;
      if (this.refs <= 0) {
        this.refs = 0;
        this.disconnect();
      }
    };
  }

  reconnect = (): void => {
    this.clearRetry();
    this.setState({ attempts: 0 });
    if (this.refs > 0) this.connect();
  };

  private connect(): void {
    if (typeof EventSource === 'undefined') return;
    this.teardownSource();
    this.bindWakeEvents();
    this.setState({ status: this.state.attempts > 0 ? 'reconnecting' : 'connecting' });

    const source = new EventSource(api.eventsUrl(this.lastEventId), { withCredentials: true });
    this.source = source;

    source.onopen = () => {
      this.setState({ status: 'open', attempts: 0 });
    };

    source.onerror = () => {
      // EventSource hides the status code; treat every failure the same and back off.
      this.teardownSource();
      this.scheduleRetry();
    };

    const onMessage = (event: Event) => this.receive(event as MessageEvent<string>);
    source.addEventListener('message', onMessage);
    // Servers that name their events land here instead of `message`.
    for (const name of REALTIME_EVENT_NAMES) source.addEventListener(name, onMessage);
  }

  private receive(event: MessageEvent<string>): void {
    if (event.lastEventId) this.lastEventId = event.lastEventId;
    let parsed: RealtimeEvent;
    try {
      parsed = JSON.parse(event.data) as RealtimeEvent;
    } catch {
      return;
    }
    if (!parsed || typeof parsed !== 'object' || typeof parsed.type !== 'string') return;

    this.buffer.push(parsed);
    if (this.buffer.length >= MAX_BUFFER) this.flush();
    else this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.flushTimer !== undefined) return;
    this.flushTimer = window.setTimeout(() => {
      this.flushTimer = undefined;
      this.flush();
    }, FLUSH_MS);
  }

  private flush(): void {
    if (this.flushTimer !== undefined) {
      window.clearTimeout(this.flushTimer);
      this.flushTimer = undefined;
    }
    const events = this.buffer;
    this.buffer = [];
    if (events.length === 0) return;

    const lastAt = new Date().toISOString();
    let presence: PresenceUser[] | undefined;

    const qc = this.queryClient;
    if (qc) {
      for (const event of coalesce(events)) {
        if (event.type === 'presence') presence = event.users;
        else applyEvent(qc, event);
      }
    } else {
      for (const event of events) if (event.type === 'presence') presence = event.users;
    }

    this.setState(presence ? { lastEventAt: lastAt, presence } : { lastEventAt: lastAt });
  }

  private scheduleRetry(): void {
    const attempts = this.state.attempts + 1;
    if (attempts > MAX_ATTEMPTS) {
      this.setState({ status: 'offline', attempts });
      return;
    }
    this.setState({ status: 'reconnecting', attempts });

    const base = Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * 2 ** (attempts - 1));
    // Jitter keeps a fleet of tabs from stampeding the server in lockstep.
    const delay = Math.round(base * (0.8 + Math.random() * 0.4));
    this.clearRetry();
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = undefined;
      if (this.refs > 0) this.connect();
    }, delay);
  }

  private clearRetry(): void {
    if (this.retryTimer !== undefined) {
      window.clearTimeout(this.retryTimer);
      this.retryTimer = undefined;
    }
  }

  /** Coming back from a sleeping laptop or a dead tunnel should be instant. */
  private bindWakeEvents(): void {
    if (this.wakeBound || typeof window === 'undefined') return;
    this.wakeBound = true;

    const wake = () => {
      if (this.refs === 0) return;
      if (this.state.status === 'open' || this.state.status === 'connecting') return;
      if (document.visibilityState === 'hidden') return;
      this.reconnect();
    };
    window.addEventListener('online', wake);
    document.addEventListener('visibilitychange', wake);
  }

  private teardownSource(): void {
    if (!this.source) return;
    this.source.onopen = null;
    this.source.onerror = null;
    this.source.close();
    this.source = null;
  }

  private disconnect(): void {
    this.clearRetry();
    if (this.flushTimer !== undefined) {
      window.clearTimeout(this.flushTimer);
      this.flushTimer = undefined;
    }
    this.buffer = [];
    this.teardownSource();
    this.setState({ status: 'idle', attempts: 0 });
  }
}

const REALTIME_EVENT_NAMES = [
  'inquiry.created',
  'inquiry.updated',
  'inquiry.deleted',
  'activity.created',
  'note.created',
  'task.updated',
  'presence',
  'heartbeat',
] as const;

export const realtime = new RealtimeClient();

/* ────────────────────────────────────────────────────────────────────────────
   Coalescing
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Collapse a burst: one write per record, deletes beat updates, and only the
 * newest presence/heartbeat survives. Activity and note events are feeds, so
 * they are kept in arrival order.
 */
function coalesce(events: RealtimeEvent[]): RealtimeEvent[] {
  const byInquiry = new Map<ID, RealtimeEvent>();
  const passthrough: RealtimeEvent[] = [];
  let presence: RealtimeEvent | undefined;

  for (const event of events) {
    switch (event.type) {
      case 'inquiry.created':
      case 'inquiry.updated': {
        const existing = byInquiry.get(event.inquiry.id);
        if (existing?.type === 'inquiry.deleted') break;
        // A create followed by an update is still a create — keep the newest body.
        const type = existing?.type === 'inquiry.created' ? 'inquiry.created' : event.type;
        byInquiry.set(event.inquiry.id, { type, inquiry: event.inquiry } as RealtimeEvent);
        break;
      }
      case 'inquiry.deleted':
        byInquiry.set(event.id, event);
        break;
      case 'presence':
        presence = event;
        break;
      case 'heartbeat':
        break;
      default:
        passthrough.push(event);
    }
  }

  const result = [...byInquiry.values(), ...passthrough];
  if (presence) result.push(presence);
  return result;
}

/* ────────────────────────────────────────────────────────────────────────────
   Cache application
   ──────────────────────────────────────────────────────────────────────────── */

function applyEvent(qc: QueryClient, event: RealtimeEvent): void {
  switch (event.type) {
    case 'inquiry.created':
      reconcileLists(qc, event.inquiry, true);
      reconcileBoards(qc, event.inquiry, true);
      markAnalyticsStale(qc);
      break;

    case 'inquiry.updated':
      patchInquiryEverywhere(qc, event.inquiry.id, event.inquiry);
      reconcileLists(qc, event.inquiry, false);
      reconcileBoards(qc, event.inquiry, false);
      markAnalyticsStale(qc);
      break;

    case 'inquiry.deleted':
      removeInquiryEverywhere(qc, event.id);
      markAnalyticsStale(qc);
      break;

    case 'activity.created':
      prependActivity(qc, event.activity);
      patchInquiryEverywhere(qc, event.activity.inquiryId, {
        lastActivityAt: event.activity.createdAt,
      });
      break;

    case 'note.created':
      prependNote(qc, event.note);
      patchInquiryEverywhere(qc, event.note.inquiryId, { lastActivityAt: event.note.createdAt });
      break;

    case 'task.updated':
      upsertTask(qc, event.task);
      break;

    default:
      break;
  }
}

function markAnalyticsStale(qc: QueryClient): void {
  // No refetch: the numbers refresh when the user next looks at them.
  void qc.invalidateQueries({ queryKey: queryKeys.analytics.all(), refetchType: 'none' });
}

/** The server's default order. Anything else and we cannot place a row correctly. */
function isDefaultSorted(part: ListKeyPart): boolean {
  if (!part.sort || part.sort.length === 0) return true;
  return (
    part.sort.length === 1 &&
    part.sort[0]!.field === 'createdAt' &&
    part.sort[0]!.direction === 'desc'
  );
}

/** Free-text and saved-view filters are evaluated server-side only. */
function isOpaque(part: ListKeyPart): boolean {
  return part.q.length > 0 || Boolean(part.viewId);
}

function removeFromList(qc: QueryClient, key: readonly unknown[], id: ID): void {
  qc.setQueryData<Pages<Inquiry>>(key, (old) => {
    if (!old) return old;
    let touched = false;
    const pages = old.pages.map((page) => {
      if (!page.items.some((item) => item.id === id)) return page;
      touched = true;
      return {
        ...page,
        items: page.items.filter((item) => item.id !== id),
        total: Math.max(0, page.total - 1),
      };
    });
    return touched ? { ...old, pages } : old;
  });
}

function reconcileLists(qc: QueryClient, inquiry: Inquiry, isNew: boolean): void {
  for (const query of qc.getQueryCache().findAll({ queryKey: queryKeys.inquiries.lists() })) {
    const part = query.queryKey[3] as ListKeyPart | undefined;
    const data = query.state.data as Pages<Inquiry> | undefined;
    if (!part || !data) continue;

    if (isOpaque(part)) {
      if (isNew) void qc.invalidateQueries({ queryKey: query.queryKey, refetchType: 'none' });
      continue;
    }

    const present = data.pages.some((page) => page.items.some((item) => item.id === inquiry.id));
    const matches = matchesInquiry(inquiry, part.filters);

    if (present && !matches) {
      removeFromList(qc, query.queryKey, inquiry.id);
      continue;
    }
    if (!present && matches) {
      if (isDefaultSorted(part)) prependInquiry(qc, query.queryKey, inquiry);
      else void qc.invalidateQueries({ queryKey: query.queryKey, refetchType: 'none' });
    }
  }
}

function reconcileBoards(qc: QueryClient, inquiry: Inquiry, isNew: boolean): void {
  for (const query of qc.getQueryCache().findAll({ queryKey: queryKeys.inquiries.boards() })) {
    const part = query.queryKey[3] as ListKeyPart | undefined;
    const data = query.state.data as BoardResponse | undefined;
    if (!part || !data) continue;

    if (isOpaque(part)) {
      if (isNew) void qc.invalidateQueries({ queryKey: query.queryKey, refetchType: 'none' });
      continue;
    }

    const matches = matchesInquiry(inquiry, part.filters);
    const present = data.columns.some((column) =>
      column.items.some((item) => item.id === inquiry.id),
    );
    // In-place edits (including a stage move) were already applied by
    // `patchInquiryEverywhere`; only membership changes are left.
    if (present === matches) continue;

    qc.setQueryData<BoardResponse>(query.queryKey, (old) => {
      if (!old) return old;

      if (present) {
        return {
          ...old,
          columns: old.columns.map((column) =>
            column.items.some((item) => item.id === inquiry.id)
              ? {
                  ...column,
                  items: column.items.filter((item) => item.id !== inquiry.id),
                  total: Math.max(0, column.total - 1),
                }
              : column,
          ),
          total: Math.max(0, old.total - 1),
        };
      }

      let inserted = false;
      const columns = old.columns.map((column) => {
        if (column.stage.key !== inquiry.stageKey) return column;
        inserted = true;
        return { ...column, items: [inquiry, ...column.items], total: column.total + 1 };
      });
      return inserted ? { ...old, columns, total: old.total + 1 } : old;
    });
  }
}

/* ────────────────────────────────────────────────────────────────────────────
   Hook
   ──────────────────────────────────────────────────────────────────────────── */

export interface UseRealtimeOptions {
  /** Disable while signed out or on the login screen. */
  enabled?: boolean;
}

export interface UseRealtimeResult extends RealtimeState {
  reconnect: () => void;
  /** Convenience for the status dot in the top bar. */
  isLive: boolean;
}

/**
 * Subscribe to the live stream. Safe to call from several components — the
 * connection is shared and events are applied to the cache exactly once.
 */
export function useRealtime(options: UseRealtimeOptions = {}): UseRealtimeResult {
  const { enabled = true } = options;
  const qc = useQueryClient();

  const state = useSyncExternalStore(realtime.subscribe, realtime.getState, realtime.getState);

  useEffect(() => {
    if (!enabled) return;
    return realtime.acquire(qc);
  }, [enabled, qc]);

  const reconnect = useCallback(() => realtime.reconnect(), []);

  return useMemo(
    () => ({ ...state, reconnect, isLive: state.status === 'open' }),
    [state, reconnect],
  );
}

/** Presence alone, for the avatar stack — avoids re-rendering on every heartbeat. */
export function usePresence(): PresenceUser[] {
  const state = useSyncExternalStore(realtime.subscribe, realtime.getState, realtime.getState);
  return state.presence;
}
